
































































import { Component, Prop, Vue } from "vue-property-decorator";
import rest from "@/rest";
// components
import SimpleMessageBox from "@/components/Shared/SimpleMessageBox.vue";
import TrainingEventExBundleEditDialog from "@/components/Academy/TrainingEventExBundleEditDialog.vue";
// interfaces
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { TrainingELearningPhase, TrainingEventExBundleVm } from "@/interfaces/TrainingEventExBundleVm";

// E-learnings of an event (training leads only). Emits "changed" after every change.
@Component({
  components: {
    SimpleMessageBox,
    TrainingEventExBundleEditDialog
  }
})
export default class TrainingEventExBundlesPanel extends Vue {
  @Prop({ required: true, type: Number }) readonly eventId!: number;
  @Prop({ required: true, type: Number }) readonly orgId!: number;
  // false for archived and cancelled events
  @Prop({ type: Boolean, default: true }) readonly editable!: boolean;

  readonly phases = [
    { text: "Vorbereitung", value: TrainingELearningPhase.Preparation },
    { text: "Nachbereitung", value: TrainingELearningPhase.FollowUp }
  ];

  entries: TrainingEventExBundleVm[] = [];
  loading = false;

  showEditDialog = false;
  editedEntry: TrainingEventExBundleVm | null = null;
  showRemoveDialog = false;
  selectedEntry: TrainingEventExBundleVm | null = null;

  async mounted(): Promise<void> {
    await this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      const params: ParamDictionary = { dictionary: {} };
      params.dictionary!["EventId"] = this.eventId.toString();
      // On errors the rest wrapper shows the message and returns undefined
      this.entries = (await rest.url("trainingLead/getEventExBundles").post(params)) ?? [];
    } finally {
      this.loading = false;
    }
  }

  entriesOfPhase(phase: TrainingELearningPhase): TrainingEventExBundleVm[] {
    return this.entries.filter(e => e.phase === phase);
  }

  anchorText(entry: TrainingEventExBundleVm): string {
    return entry.phase === TrainingELearningPhase.Preparation ? "Beginn" : "Ende";
  }

  offsetText(days: number): string {
    if (days === 0) return "am Tag";
    return days < 0 ? `${-days} Tage vor` : `${days} Tage nach`;
  }

  assignmentText(entry: TrainingEventExBundleVm): string {
    if (entry.absoluteAssignmentDate) return `Zuweisung am ${this.$globalHelper.toLocalDateTimeWeekdayString(entry.absoluteAssignmentDate)}`;
    if (entry.assignmentOffsetDays === null) return "Zuweisung sofort nach der Anmeldung";
    return `Zuweisung ${this.offsetText(entry.assignmentOffsetDays)} ${this.anchorText(entry)}`;
  }

  deadlineText(entry: TrainingEventExBundleVm): string {
    if (entry.absoluteDeadline) return `Stichtag ${this.$globalHelper.toLocalDateTimeWeekdayString(entry.absoluteDeadline)}`;
    if (entry.deadlineOffsetDays === null) return "Kein Stichtag";
    return `Stichtag ${this.offsetText(entry.deadlineOffsetDays)} ${this.anchorText(entry)}`;
  }

  openEditor(entry: TrainingEventExBundleVm | null): void {
    this.editedEntry = entry;
    this.showEditDialog = true;
  }

  async onSaved(): Promise<void> {
    await this.load();
    this.$emit("changed");
  }

  askRemove(entry: TrainingEventExBundleVm): void {
    this.selectedEntry = entry;
    this.showRemoveDialog = true;
  }

  async remove(): Promise<void> {
    if (!this.selectedEntry) return;
    const params: ParamDictionary = { dictionary: {} };
    params.dictionary!["EventExBundleId"] = this.selectedEntry.id.toString();
    // The response is an empty string on success and undefined after an error (message shown by the rest wrapper)
    const result = await rest.url("trainingLead/removeEventExBundle").post(params);
    if (result !== undefined) await this.onSaved();
  }
}
